import {
  ArrowRight,
  ClipboardList,
  MessageSquare,
  Users,
} from 'lucide-react';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  buildClaimsFilingUrl,
  buildCoverageOverviewUrl,
} from '@/components/claims-filing/buildClaimsFilingUrl';
import { SmsComposerModal } from '@/components/sms-composer';
import { UpworkEscalationCard } from '@/components/tasks/task-detail/UpworkEscalationCard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { VoiceAgentButton } from '@/components/voice-agent';
import { MRFollowupOutcomeCard } from '@/components/voice-agent/outcomes';
import { isTreatmentSmsEnabledForUser } from '@/lib/featureFlags';
import { cn } from '@/lib/utils';
import { useGetMatterFlattenedContextQuery } from '@/services/api/matterFlattenedContextService';
import { useListCheckInsV2Query } from '@/services/api/treatmentCheckInV2Api';
import {
  useCreateTreatmentCheckInMutation,
  useGetTreatmentCheckInsQuery,
} from '@/services/api/treatmentService';
import { Task } from '@/services/types/client-intake-types';
import { CoverageSchema } from '@/services/types/coverage-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskType,
  TASK_TEMPLATE_TYPES,
} from '@/services/types/task-types';
import { TreatmentCheckInStatus } from '@/services/types/treatment-types';
import { selectEmail } from '@/state/selectors/root-selectors';

import { CallClientButton } from '../CallClientButton';
import { ClientUploadsReview } from './ClientUploadsReview';
import { TaskCorrespondenceActions } from './TaskCorrespondenceActions';
import { TaskFollowUpCard } from './TaskFollowUpCard';
import { TreatmentTextBetaModal } from './TreatmentTextBetaModal';

interface TaskActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: Task;
  matterId?: string;
  coverages?: CoverageSchema[];
  onStartInitialReview?: () => void;
  onOpenReviewGroupCoverages?: () => void;
  onOpenVerify1PCoverage?: () => void;
  onLinkCoverageRequest?: () => void;
  isLinkCoverageDisabled?: boolean;
}

export const TaskActionModal: React.FC<TaskActionModalProps> = ({
  open,
  onOpenChange,
  task,
  matterId,
  coverages,
  onStartInitialReview,
  onOpenReviewGroupCoverages,
  onOpenVerify1PCoverage,
  onLinkCoverageRequest,
  isLinkCoverageDisabled,
}) => {
  const navigate = useNavigate();
  const userEmail = useSelector(selectEmail);
  const [isSmsModalOpen, setIsSmsModalOpen] = React.useState(false);
  const [isCreatingCheckIn, setIsCreatingCheckIn] = React.useState(false);

  // Hooks - must be unconditional
  const { data: matterContext } = useGetMatterFlattenedContextQuery(
    { matterId: matterId || '' },
    { skip: !matterId },
  );
  const [createCheckIn] = useCreateTreatmentCheckInMutation();

  // Derive task type info
  const canonicalType =
    typeof task.type === 'object'
      ? (task.type?.canonical_type as CanonicalTaskType | undefined)
      : undefined;
  const taskTypeName =
    typeof task.type === 'string' ? task.type : (task.type?.name ?? task.name);

  const normalizeLabel = (value?: string | null) =>
    (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const isTreatmentCheckInTask = (() => {
    if (
      canonicalType &&
      canonicalType !== CanonicalTaskType.TREATMENT_CHECK_IN
    )
      return false;
    if (canonicalType === CanonicalTaskType.TREATMENT_CHECK_IN) return true;
    return (
      normalizeLabel(taskTypeName).includes('treatmentcheckin') ||
      (task.name && task.name.toLowerCase().includes('treatment check'))
    );
  })();

  const { data: treatmentCheckIns = [] } = useGetTreatmentCheckInsQuery(
    matterId || '',
    { skip: !matterId || !isTreatmentCheckInTask },
  );
  const { data: treatmentCheckInsV2 = [] } = useListCheckInsV2Query(
    matterId || '',
    { skip: !matterId || !isTreatmentCheckInTask },
  );
  const existingActiveCheckIn = React.useMemo(
    () =>
      treatmentCheckIns
        .filter(
          (checkIn) =>
            checkIn.triggered_by_task_id === task.id &&
            checkIn.status !== TreatmentCheckInStatus.COMPLETED,
        )
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() -
            new Date(a.created_at).getTime(),
        )[0],
    [task.id, treatmentCheckIns],
  );

  const isClaimsReviewTask =
    canonicalType === CanonicalTaskType.REVIEW_FOUND_COVERAGE ||
    canonicalType === CanonicalTaskType.OPEN_CLAIM ||
    canonicalType === CanonicalTaskType.SEND_LOR ||
    canonicalType === CanonicalTaskType.LOR_ACKNOWLEDGEMENT;

  const isMedicalRecordsVoiceAITask = Boolean(
    task.treatment_record_request?.id &&
      (task.medical_note_id || task.treatment_record_request?.provider) &&
      matterId,
  );

  const isInsuranceGroupTask =
    task.group?.canonical_group === CanonicalTaskGroup.FIRST_PARTY_INSURANCE ||
    task.group?.canonical_group === CanonicalTaskGroup.THIRD_PARTY_INSURANCE;

  const isSmsEnabled = isTreatmentSmsEnabledForUser(userEmail);
  const hasPriorTreatmentCheckIns =
    treatmentCheckIns.length > 0 || treatmentCheckInsV2.length > 0;
  const smsActionDetails = (() => {
    if (isTreatmentCheckInTask)
      return { description: 'Treatment check-in text draft' };
    if (taskTypeName === 'Document Collection')
      return {
        description: 'Document collection message',
        category: 'Document Collection' as const,
      };
    if (canonicalType === CanonicalTaskType.HEALTH_INSURANCE_CARD)
      return {
        description: 'Health insurance card request',
        category: 'Document Collection' as const,
      };
    if (canonicalType === CanonicalTaskType.HIPAA_DIRECTIVE)
      return {
        description: 'HIPAA directive request',
        category: 'Document Collection' as const,
      };
    return null;
  })();
  const showTreatmentTextBeta =
    isTreatmentCheckInTask &&
    hasPriorTreatmentCheckIns;
  const showGenericSmsAction =
    isSmsEnabled && Boolean(smsActionDetails) && !isTreatmentCheckInTask;
  const clientName = matterContext?.clientName || 'Client';
  const clientPhone = matterContext?.clientPhone ?? undefined;

  const handleGoToCheckIn = async () => {
    if (!matterId) return;
    try {
      setIsCreatingCheckIn(true);
      if (existingActiveCheckIn) {
        navigate(
          `/matters/${matterId}/treatment/check-ins/${existingActiveCheckIn.id}/edit`,
        );
        onOpenChange(false);
        return;
      }
      const newCheckIn = await createCheckIn({
        matterId,
        triggered_by_task_id: task.id,
      }).unwrap();
      navigate(
        `/matters/${matterId}/treatment/check-ins/${newCheckIn.id}/edit`,
      );
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create treatment check-in:', error);
      toast.error('Failed to create treatment check-in');
    } finally {
      setIsCreatingCheckIn(false);
    }
  };

  const handleGoToClaimsReview = () => {
    if (!matterId) return;
    const claimsCoverageId =
      (task.coverage?.id as string | undefined) ||
      (task.coverage_id as string | undefined);
    if (!claimsCoverageId) return;
    let path: string;
    if (
      canonicalType === CanonicalTaskType.LOR_ACKNOWLEDGEMENT ||
      canonicalType === CanonicalTaskType.REVIEW_FOUND_COVERAGE
    ) {
      path = buildCoverageOverviewUrl(matterId, claimsCoverageId);
    } else {
      const stepParam =
        canonicalType === CanonicalTaskType.OPEN_CLAIM
          ? 'call'
          : 'lor';
      path = buildClaimsFilingUrl(matterId, claimsCoverageId, stepParam);
    }
    navigate(path);
    onOpenChange(false);
  };

  // Determine modal title
  const getTitle = () => {
    if (canonicalType === CanonicalTaskType.CONDUCT_INITIAL_REVIEW)
      return 'Start Initial Review';
    if (isInsuranceGroupTask && (task.companion_count ?? 0) > 1)
      return 'Review Group Coverages';
    if (
      task.template_type === TASK_TEMPLATE_TYPES.COVERAGE_VERIFY_1P_COVERAGE
    )
      return 'Verify 1P Coverage';
    if (task.template_type === TASK_TEMPLATE_TYPES.CLIENT_PORTAL_REVIEW_UPLOADS)
      return 'Review Client Uploads';
    if (isClaimsReviewTask) return 'Claims Review';
    if (canonicalType === CanonicalTaskType.OPEN_FILE) return 'Send Notice';
    if (isMedicalRecordsVoiceAITask) return 'Follow Up with Provider';
    if (isTreatmentCheckInTask) return 'Treatment Check-In';
    return 'Task Actions';
  };

  const renderContent = () => {
    // CONDUCT_INITIAL_REVIEW
    if (canonicalType === CanonicalTaskType.CONDUCT_INITIAL_REVIEW) {
      return (
        <Button
          onClick={() => {
            onStartInitialReview?.();
            onOpenChange(false);
          }}
          disabled={!matterId}
          className="w-full bg-zinc-900 text-white hover:bg-zinc-800"
        >
          <ClipboardList className="h-4 w-4 mr-2" />
          Start Initial Review
        </Button>
      );
    }

    // Insurance group with companions
    if (isInsuranceGroupTask && (task.companion_count ?? 0) > 1) {
      return (
        <div className="space-y-3">
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                <Users className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <div className="text-sm font-semibold text-blue-900">
                  Companion Coverages
                </div>
                <div className="text-xs font-medium text-blue-600">
                  Review and share coverages across {task.companion_count}{' '}
                  companion cases
                </div>
              </div>
            </div>
          </div>
          <Button
            onClick={() => {
              onOpenReviewGroupCoverages?.();
              onOpenChange(false);
            }}
            className="w-full bg-blue-600 text-white hover:bg-blue-700"
          >
            Review Group Coverages
          </Button>
        </div>
      );
    }

    // Verify 1P Coverage
    if (
      task.template_type === TASK_TEMPLATE_TYPES.COVERAGE_VERIFY_1P_COVERAGE
    ) {
      return (
        <Button
          onClick={() => {
            onOpenVerify1PCoverage?.();
            onOpenChange(false);
          }}
          disabled={!matterId}
          className="w-full bg-zinc-900 text-white hover:bg-zinc-800"
        >
          <ClipboardList className="h-4 w-4 mr-2" />
          Verify 1P Coverage
        </Button>
      );
    }

    // Client portal upload review
    if (task.template_type === TASK_TEMPLATE_TYPES.CLIENT_PORTAL_REVIEW_UPLOADS) {
      return (
        <ClientUploadsReview
          taskId={task.id}
          matterId={matterId || ''}
          blobIds={(task.metadata?.client_portal_blob_ids as string[] | undefined) || []}
          onComplete={() => onOpenChange(false)}
        />
      );
    }

    // Open File (correspondence)
    if (canonicalType === CanonicalTaskType.OPEN_FILE) {
      const coverageId =
        (task.coverage?.id as string | undefined) || task.coverage_id;
      return (
        <div className="space-y-3">
          {matterId && (
            <TaskCorrespondenceActions
              task={task}
              matterId={matterId}
              coverages={coverages}
            />
          )}
          {onLinkCoverageRequest && !coverageId && (
            <Button
              variant="outline"
              size="sm"
              disabled={isLinkCoverageDisabled}
              onClick={() => {
                onLinkCoverageRequest();
                onOpenChange(false);
              }}
              className="w-full"
            >
              Link to coverage
            </Button>
          )}
        </div>
      );
    }

    // Claims review
    if (isClaimsReviewTask) {
      const claimsCoverageId =
        (task.coverage?.id as string | undefined) ||
        (task.coverage_id as string | undefined);
      return (
        <div className="space-y-3">
          <Button
            onClick={handleGoToClaimsReview}
            disabled={!matterId || !claimsCoverageId}
            className="w-full bg-blue-600 text-white hover:bg-blue-700"
          >
            Go to Claims Review
          </Button>
          {onLinkCoverageRequest && (
            <Button
              variant="outline"
              size="sm"
              disabled={isLinkCoverageDisabled}
              onClick={() => {
                onLinkCoverageRequest();
                onOpenChange(false);
              }}
              className="w-full"
            >
              Link to coverage
            </Button>
          )}
        </div>
      );
    }

    // Medical records / Voice AI tasks
    if (isMedicalRecordsVoiceAITask) {
      return (
        <div className="space-y-4">
          {task.type?.canonical_type === CanonicalTaskType.MEDICAL_RECORDS && (
            <UpworkEscalationCard taskId={task.id} matterId={matterId!} />
          )}
          <MRFollowupOutcomeCard
            matterId={matterId!}
            providerId={
              task.medical_note_id ||
              task.treatment_record_request?.provider ||
              ''
            }
            requestId={task.treatment_record_request!.id}
            taskId={task.id}
          />
          <VoiceAgentButton task={task} matterId={matterId!} />
          <div className="border-t border-slate-200 pt-4">
            <div className="text-xs font-semibold text-slate-700 mb-2">
              Follow-Up Transmission
            </div>
            <TaskFollowUpCard
              matterId={matterId!}
              providerId={
                task.medical_note_id ||
                task.treatment_record_request?.provider ||
                ''
              }
              requestId={task.treatment_record_request!.id}
            />
          </div>
        </div>
      );
    }

    // Default: Treatment check-in + SMS
    return (
      <div className="space-y-3">
        {isTreatmentCheckInTask && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleGoToCheckIn}
              disabled={!matterId || isCreatingCheckIn}
            >
              <ArrowRight className="h-3.5 w-3.5 mr-1.5" />
              {isCreatingCheckIn ? 'Creating...' : 'Go to Check-In'}
            </Button>
            {clientPhone && <CallClientButton phoneNumber={clientPhone} />}
          </div>
        )}
        {showTreatmentTextBeta && smsActionDetails && (
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    Draft Treatment Text
                  </div>
                  <div className="text-xs text-gray-500">
                    Generate a copy-ready initial or follow-up text
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => {
                  onOpenChange(false);
                  setIsSmsModalOpen(true);
                }}
              >
                <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                Draft
              </Button>
            </div>
          </div>
        )}
        {isTreatmentCheckInTask && !hasPriorTreatmentCheckIns && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3">
            <div className="text-sm font-medium text-zinc-900">
              Text beta starts after the first check-in
            </div>
            <div className="mt-1 text-xs text-zinc-500">
              Complete the initial treatment check-in first, then this copy-ready
              text flow will be available here.
            </div>
          </div>
        )}
        {showGenericSmsAction && smsActionDetails && (
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-gray-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    Send SMS
                  </div>
                  <div className="text-xs text-gray-500">
                    {smsActionDetails.description}
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => {
                  onOpenChange(false);
                  setIsSmsModalOpen(true);
                }}
              >
                <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                Send
              </Button>
            </div>
          </div>
        )}
        <VoiceAgentButton task={task} matterId={matterId!} />
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={cn(
          "max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto",
          task.template_type === TASK_TEMPLATE_TYPES.CLIENT_PORTAL_REVIEW_UPLOADS
            ? "sm:max-w-6xl max-h-[90vh]"
            : "sm:max-w-[700px]",
        )}>
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">
              {getTitle()}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">{renderContent()}</div>
        </DialogContent>
      </Dialog>

      {showTreatmentTextBeta && matterId && (
        <TreatmentTextBetaModal
          open={isSmsModalOpen}
          onOpenChange={setIsSmsModalOpen}
          matterId={matterId}
          taskId={task.id}
          clientName={clientName}
          clientPhone={clientPhone}
        />
      )}

      {/* SMS Composer modal - rendered outside the action dialog to avoid nesting issues */}
      {showGenericSmsAction && smsActionDetails && matterId && (
        <SmsComposerModal
          open={isSmsModalOpen}
          onOpenChange={setIsSmsModalOpen}
          matterId={matterId}
          clientName={clientName}
          clientPhone={clientPhone}
          taskId={task.id}
          initialCategory={smsActionDetails.category}
          onSmsSent={() => setIsSmsModalOpen(false)}
        />
      )}
    </>
  );
};
